/* eslint-disable react-refresh/only-export-components */
// app_client/src/components/providers/ConfirmationProvider.jsx
import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';

export const ConfirmationContext = createContext(null);

const initialDialogState = {
  isOpen: false,
  message: '',
  specialWarning: '',
  title: 'Emin misiniz?',
  confirmText: 'Onayla',
  cancelText: 'Vazgeç',
  confirmValue: true,
  cancelValue: false,
  dismissValue: false,
  requireReason: false,
  reasonLabel: 'Silme nedeni',
  reasonPlaceholder: 'Bu işlemi neden siliyorsunuz?',
  dialogKey: 0
};

const ConfirmationDialog = ({
  isOpen,
  title,
  message,
  specialWarning,
  requireReason,
  reasonLabel,
  reasonPlaceholder,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
  onDismiss
}) => {
  const [reason, setReason] = useState('');

  if (!isOpen) return null;

  const isConfirmDisabled = requireReason && reason.trim().length === 0;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 md:p-6 overflow-y-auto">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onDismiss} />

      <div className="relative bg-white w-full max-w-md mx-auto max-h-[calc(100vh-2rem)] md:max-h-[calc(100vh-3rem)] rounded-[2.5rem] shadow-2xl border border-slate-100 overflow-y-auto animate-in fade-in zoom-in duration-200">
        <div className="p-8">
          <div className="flex justify-between items-start mb-6">
            <div className="w-12 h-12 bg-amber-50 rounded-2xl flex items-center justify-center text-amber-500">
              <AlertTriangle size={24} />
            </div>
            <button
              type="button"
              onClick={onDismiss}
              className="p-2 hover:bg-slate-50 rounded-xl text-slate-400 transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          <h3 className="text-xl font-black text-slate-800 mb-2">{title}</h3>
          <p className="text-slate-500 text-sm font-medium leading-relaxed">{message}</p>
          {specialWarning && (
            <div className="mt-5 rounded-2xl border border-amber-100 bg-amber-50 p-4 text-sm font-bold leading-relaxed text-amber-800 whitespace-pre-line">
              {specialWarning}
            </div>
          )}
          {requireReason && (
            <label className="mt-5 block">
              <span className="mb-2 block text-[10px] font-black uppercase tracking-widest text-slate-400">
                {reasonLabel}
              </span>
              <textarea
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder={reasonPlaceholder}
                rows={4}
                className="w-full resize-none rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm font-bold text-slate-700 outline-none transition focus:border-slate-300 focus:bg-white focus:ring-4 focus:ring-slate-900/5"
              />
            </label>
          )}
        </div>

        <div className="p-6 bg-slate-50/50 border-t border-slate-50 flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 px-6 py-3 rounded-2xl font-black text-xs uppercase tracking-widest text-slate-500 hover:bg-slate-100 transition-all"
          >
            {cancelText}
          </button>
          <button
            type="button"
            onClick={() => onConfirm(reason.trim())}
            disabled={isConfirmDisabled}
            className="flex-1 px-6 py-3 rounded-2xl bg-slate-900 hover:bg-slate-800 text-white font-black text-xs uppercase tracking-widest shadow-lg shadow-slate-900/20 transition-all active:scale-95 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export const ConfirmationProvider = ({ children }) => {
  const resolverRef = useRef(null);
  const [dialogState, setDialogState] = useState(initialDialogState);

  const closeDialog = useCallback((result) => {
    resolverRef.current?.(result);
    resolverRef.current = null;
    setDialogState(initialDialogState);
  }, []);

  const confirm = useCallback((options) => {
    const nextState = typeof options === 'string'
      ? { ...initialDialogState, isOpen: true, message: options, dialogKey: Date.now() }
      : { ...initialDialogState, ...options, isOpen: true, dialogKey: Date.now() };

    return new Promise((resolve) => {
      resolverRef.current?.(false);
      resolverRef.current = resolve;
      setDialogState(nextState);
    });
  }, []);

  const contextValue = {
    confirm,
    isOpen: dialogState.isOpen
  };

  return (
    <ConfirmationContext.Provider value={contextValue}>
      {children}
      <ConfirmationDialog
        key={dialogState.dialogKey}
        isOpen={dialogState.isOpen}
        title={dialogState.title}
        message={dialogState.message}
        specialWarning={dialogState.specialWarning}
        requireReason={dialogState.requireReason}
        reasonLabel={dialogState.reasonLabel}
        reasonPlaceholder={dialogState.reasonPlaceholder}
        confirmText={dialogState.confirmText}
        cancelText={dialogState.cancelText}
        onConfirm={(reason) => closeDialog(
          dialogState.requireReason
            ? { confirmed: true, value: dialogState.confirmValue, reason }
            : dialogState.confirmValue
        )}
        onCancel={() => closeDialog(dialogState.cancelValue)}
        onDismiss={() => closeDialog(dialogState.dismissValue)}
      />
    </ConfirmationContext.Provider>
  );
};

export const useConfirmation = () => {
  const context = useContext(ConfirmationContext);

  if (!context) {
    throw new Error('useConfirmation must be used within a ConfirmationProvider');
  }

  return context;
};

export default ConfirmationProvider;
